<template>
  <div :class="$style.context_menus_wrapper">
    <context-menu
      v-for="context in contextMenus"
      :key="`context_menu_${context.type}_${context.menu}`"
      :class="[$style.context_menu, isMultiselectContext(context) ? '' : 'gantt_scroll']"
      :boundaryElementSelector="'.board_main_container'"
      :position="context.position"
      :side="context.side"
      :data-menu-type="`${context.type}:${context.menu}`"
      :style="getPopupStyle(context)"
      @onClickOutside="(e) => handleClosePopup(context, e)"
      @onScroll="() => handleClosePopup(context)"
    >
      <template #body>
        <MultiselectContextMenu
          v-if="isMultiselectContext(context)"
          :items="getMenuItems(context)"
          @selectItems="(values) => selectedMultiselect(values, context)"
        />
        <div
          v-else
          :class="['gantt_scroll']"
        >
          <div
            v-for="item in getMenuItems(context)"
            :key="getUniqueKey(item)"
            :class="[
              $style.menu_item,
              item.selected && !(item.isMultiselect && item.value !== 'empty') ? $style.selected : '',
              item.selected ? 'selected_menu_item' : '',
              item.disabled ? $style.disabled : '',
              item.divider ? $style.divider : ''
            ]"
            :data-menu-type="`${item.type}:${item.menu}`"
            v-on="mouseHandlers(context, item)"
          >
            <div
              v-if="item.divider"
              :class="$style.divider"
            >
              <div
                v-if="item.name"
                :class="$style.divider__label"
              >
                {{ item.name }}
              </div>
              <div :class="$style.divider__line" />
            </div>
            <div
              v-else
              :class="$style.item_content"
            >
              <div
                v-if="item.isMultiselect && item.value !== 'empty'"
                :class="[$style.checkbox, item.selected ? $style.selected : '']"
              >
                <svg-sprite
                  name="check"
                  type="regular"
                  :size="16"
                  :style="{ color: '#FFFFFF' }"
                />
              </div>
              <div
                v-if="item.icon"
                :class="$style.icon"
                :style="{
                  background: item.isColorField ? item.name : ''
                }"
              >
                <img
                  v-if="item.iconType === 'img'"
                  :src="item.icon"
                >
                <svg-sprite
                  v-else
                  :name="item.icon"
                  :type="item.iconType"
                  :size="item.iconSize"
                  :style="{ color: item.iconColor || '' }"
                />
              </div>
              <div
                v-if="!item.isColorField"
                :class="[$style.label, item.labelBg ? $style.is_label_field : '' ]"
                :style="{
                  color: item.labelColor || '',
                  background: item.labelBg || ''
                }"
              >
                {{ item.name }}
              </div>
              <div
                v-if="item.iconRight"
                :class="$style.icon_right"
              >
                <svg-sprite
                  :name="item.iconRight"
                  :type="item.iconRightType"
                  :size="item.iconRightSize"
                  :style="{ color: item.iconRightColor ? item.iconRightColor : '' }"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </context-menu>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import MultiselectContextMenu from './MultiselectContextMenu.vue';

import { taskToOptions, prepareTaskValue, changeAssign } from '../../../helpers/boardHelpers';
import rights from '../../../components/rights';

export default {
  name: 'ContextMenus',
  components: { MultiselectContextMenu },
  inject: ['toggleContextMenu'],
  emits: [
    'editField',
    'moveColumn',
    'showTaskSettings',
    'taskChange',
    'deleteTask',
  ],
  data() {
    return {
      locale: __,
      multiselectItems: [],
    };
  },
  computed: {
    contextMenuItems() {
      let secondaryItems = [];

      this.contextMenus.forEach(context => {
        const contextKey = `${context.type}:${context.menu}`;

        switch (contextKey) {
        case 'column:moveTo':
          secondaryItems = this.groupingFieldInfo.options.map((opt, index) => ({
            type: 'column',
            menu: 'moveTo',
            name: `${__('board_column_position', { position: (index + 1) })}`,
            id: 'board_column_position',
            value: index,
            selected: opt.id === context.item.id,
          }));
          break;
        case 'task:moveTo':
          const taskOpts = taskToOptions({
            task: context.item,
            groupingFieldInfo: this.groupingFieldInfo,
          });

          secondaryItems = this.groupingFieldInfo.options.map(opt => {
            const menuItem = {
              type: 'task',
              menu: 'moveTo',
              name: opt.value,
              id: 'board_task_value',
              value: opt.id,
              selected: taskOpts.includes(opt.id),
              isMultiselect: ['multiselect', 'array'].includes(this.groupingFieldInfo.type),
            };

            let moreProps = {};

            if (['status', 'priority'].includes(this.groupingFieldInfo.key)) {
              moreProps = {
                icon: opt.icon,
                iconType: this.groupingFieldInfo.key === 'status' ? 'regular' : 'bold',
                iconSize: this.groupingFieldInfo.key === 'status' ? 20 : 24,
                iconColor: opt.color,
              };
            }

            if (this.groupingFieldInfo.key === 'resources') {
              if (opt.id === 'empty') {
                if (!this.groupingFieldInfo.isCustom) {
                  moreProps = {
                    icon: 'assigned',
                    iconType: 'regular',
                    iconSize: 24,
                    iconColor: '#B2B2B2',
                    iconSelectedColor: '#FFF'
                  };
                }
              } else {
                moreProps = {
                  icon: opt.picture,
                  iconType: 'img',
                };
              }
            }

            if (this.groupingFieldInfo.key === 'color') {
              if (opt.id !== 'empty') {
                moreProps = {
                  icon: 'check',
                  iconType: 'regular',
                  iconSize: 24,
                  iconColor: menuItem.selected ? '#FFFFFF' : opt.value,
                  isColorField: true,
                };
              }
            }

            if (this.groupingFieldInfo.key === 'tags' && opt.id !== 'empty') {
              moreProps = {
                labelColor: '#FFFFFF',
                labelBg: opt.color,
              };
            }

            return { ...menuItem, ...moreProps };
          });
          break;
        case 'group:main':
          secondaryItems = this.groupingFields.map(item => {
            const menuItem = {
              type: 'group',
              menu: 'main',
              name: item.value,
              id: 'board_group_value',
            };

            let moreProps = {};

            if (item.type === 'divider') {
              moreProps = {
                divider: true,
              };
            } else {
              moreProps = {
                icon: item.icon.name,
                iconType: item.icon.type,
                iconSize: item.icon.size,
                iconColor: item.isGroupedBy ? '#FFFFFF' : '#333333',
                value: item.id,
                isCustomField: item.isCustom,
                selected: item.isGroupedBy,
              };
            }

            return { ...menuItem, ...moreProps };
          });
          break;
        case 'sort:main':
          secondaryItems = this.sortingFields.map(item => ({
            type: 'sort',
            menu: 'main',
            name: item.name,
            id: 'board_sort_value',
            icon: item.icon.name,
            iconType: item.icon.type,
            iconSize: item.icon.size,
            iconColor: item.isSortedBy ? '#FFFFFF' : '#333333',
            value: item.id,
            selected: item.isSortedBy,
          }));
          break;
        case 'task:status':
          secondaryItems = this.getField(false, 'status').options.map(opt => ({
            type: 'task',
            menu: 'status',
            name: opt.value,
            id: 'board_task_status',
            icon: opt.icon,
            iconType: 'regular',
            iconSize: 20,
            iconColor: opt.color,
            value: opt.id,
            selected: opt.id === context.item.status,
          }));
          break;
        case 'task:priority':
          secondaryItems = this.getField(false, 'priority').options.map(opt => ({
            type: 'task',
            menu: 'priority',
            name: opt.value,
            id: 'board_task_priority',
            icon: opt.icon,
            iconType: 'bold',
            iconSize: 24,
            iconColor: opt.color,
            value: opt.id,
            selected: opt.id === context.item.priority,
          }));
          break;
        }
      });

      return [
        {
          type: 'column',
          menu: 'main',
          name: __('board_column_edit'),
          id: 'board_column_edit',
          icon: 'pensil',
          gpTestId: 'gp_autotest_board_column_edit',
          iconType: 'regular',
          iconSize: 24,
          iconColor: '#333333',
          disabled: !this.groupingFieldInfo.isCustom || !rights.account.hasRight('custom_fields_edit'),
          handlers: ['mouseenter', 'click'],
        },
        {
          type: 'column',
          menu: 'main',
          name: __('board_column_move_to'),
          id: 'board_column_move_to',
          icon: 'arrow-right-2',
          gpTestId: 'gp_autotest_board_column_move_to',
          iconType: 'regular',
          iconSize: 24,
          iconColor: '#333333',
          iconRight: 'drop-right',
          iconRightType: 'bold',
          iconRightSize: 24,
          iconRightColor: '#B2B2B2',
          handlers: ['mouseenter'],
        },
        {
          type: 'column',
          menu: 'main',
          name: __('board_column_delete'),
          id: 'board_column_delete',
          icon: 'delete',
          gpTestId: 'gp_autotest_board_column_delete',
          iconType: 'regular',
          iconSize: 24,
          iconColor: '#DD3636',
          labelColor: '#DD3636',
          disabled: !this.groupingFieldInfo.isCustom || !rights.account.hasRight('custom_fields_edit'),
          handlers: ['mouseenter', 'click'],
        },
        {
          type: 'task',
          menu: 'main',
          name: __('board_task_settings'),
          id: 'board_task_settings',
          icon: 'open',
          gpTestId: 'gp_autotest_board_task_settings',
          iconType: 'regular',
          iconSize: 24,
          iconColor: '#333333',
          handlers: ['mouseenter', 'click'],
        },
        {
          type: 'task',
          menu: 'main',
          name: '',
          id: 'board_task_context_divider',
          gpTestId: 'gp_autotest_board_task_context_divider',
          divider: true,
          handlers: ['mouseenter', 'click'],
        },
        {
          type: 'task',
          menu: 'main',
          name: __('board_task_rename'),
          id: 'board_task_rename',
          icon: 'pensil',
          gpTestId: 'gp_autotest_board_task_rename',
          iconType: 'regular',
          iconSize: 24,
          iconColor: '#333333',
          handlers: ['mouseenter', 'click'],
          disabled: !rights.project.hasRight(this.projectId, 'static_fields')
        },
        {
          type: 'task',
          menu: 'main',
          name: __('board_task_move_to'),
          id: 'board_task_move_to',
          icon: 'arrow-right-2',
          gpTestId: 'gp_autotest_board_task_move_to',
          iconType: 'regular',
          iconSize: 24,
          iconColor: '#333333',
          iconRight: 'drop-right',
          iconRightType: 'bold',
          iconRightSize: 24,
          iconRightColor: '#B2B2B2',
          handlers: ['mouseenter'],
          disabled: !this.canDragTasks()
        },
        {
          type: 'task',
          menu: 'main',
          name: __('board_task_delete'),
          id: 'board_task_delete',
          icon: 'delete',
          gpTestId: 'gp_autotest_board_task_delete',
          iconType: 'regular',
          iconSize: 24,
          iconColor: '#DD3636',
          labelColor: '#DD3636',
          handlers: ['mouseenter', 'click'],
          disabled: !rights.project.hasRight(this.projectId, 'delete_task')
        },
        ...secondaryItems,
      ];
    },
    ...mapGetters('board', ['contextMenus', 'groupingFieldInfo', 'groupingFields', 'sortingFields', 'getField', 'projectId']),
    ...mapGetters('resourcesModel', ['getResourceById']),
  },
  methods: {
    handleClosePopup({ item, type, menu }, e) {
      if (menu === 'main' && (e?.target.closest('.selected_menu_item') || e?.target.closest('.multiselect_context'))) {
        return;
      }

      this.toggleContextMenu(item, type, menu);
    },
    getMenuItems(context) {
      const menuItems = this.contextMenuItems.filter(item => item.type === context.type && item.menu === context.menu);

      return menuItems;
    },
    isMultiselectContext(context) {
      return context.type === 'task' && context.menu === 'moveTo' && this.getMenuItems(context).every(item => item.isMultiselect);
    },
    canDragTasks() {
      let canDragInCurrentGrouping = false;

      if (this.groupingFieldInfo.key === 'status') {
        canDragInCurrentGrouping = rights.project.hasRight(this.projectId, 'static_fields') || rights.project.hasRight(this.projectId, 'static_fields_4_only');
      } else if (this.groupingFieldInfo.isCustom) {
        canDragInCurrentGrouping = rights.project.hasRight(this.projectId, 'custom_field_edit');
      } else {
        canDragInCurrentGrouping = rights.project.hasRight(this.projectId, 'static_fields');
      }

      return canDragInCurrentGrouping;
    },
    selectItem(context, selectedItem, e) {
      if (selectedItem.selected || selectedItem.disabled || selectedItem.divider) return;

      const { item, type, menu } = context;

      switch (selectedItem.id) {
      case 'board_column_edit': {
        this.$emit('editField');
        this.toggleContextMenu(item, type, menu);

        break;
      }
      case 'board_column_delete': {
        this.$emit('editField');
        this.toggleContextMenu(item, type, menu);

        break;
      }
      case 'board_column_move_to':
      case 'board_task_move_to': {
        this.toggleContextMenu(item, type, 'moveTo', e.currentTarget, 'right');

        break;
      }
      case 'board_column_position': {
        this.toggleContextMenu(item, type, menu);

        const columnIndex = this.groupingFieldInfo.options.findIndex(opt => opt.id === item.id);

        this.$emit('moveColumn', item, columnIndex, selectedItem.value);

        break;
      }
      case 'board_task_settings': {
        this.$emit('showTaskSettings', item.id);
        this.toggleContextMenu(item, type, menu);

        break;
      }
      case 'board_task_rename': {
        this.$store.dispatch('board/setNameEditKey', `${item.columnId}:${item.id}`);
        this.toggleContextMenu(item, type, menu);

        break;
      }
      case 'board_task_value':
      case 'board_task_status':
      case 'board_task_priority': {
        this.toggleContextMenu(item, type, menu);
        let key = '';

        if (selectedItem.id === 'board_task_status') {
          key = 'status';
        } else if (selectedItem.id === 'board_task_priority') {
          key = 'priority';
        } else {
          key = this.groupingFieldInfo.isCustom ? this.groupingFieldInfo.customFieldId : this.groupingFieldInfo.key;
        }
        let value = '';

        if (key === 'resources') {
          changeAssign({
            task: item,
            rawValue: selectedItem.value,
            $store: this.$store,
          });

          return;
        }

        value = prepareTaskValue({
          task: item,
          key,
          rawValue: selectedItem.value,
          field: selectedItem.id === 'board_task_value' ? this.groupingFieldInfo : {},
        });

        this.$emit('taskChange', {
          taskData: item,
          key,
          value,
        });

        break;
      }
      case 'board_task_delete': {
        this.toggleContextMenu(item, type, menu);
        this.$emit('deleteTask', item);
        break;
      }
      case 'board_group_value': {
        this.toggleContextMenu(item, type, menu);
        userExtAnalytics.log('header_select_board_grouping', {
          type: selectedItem.name,
        });

        // check if exist local grouping. Update only local store

        const currentConfig = this.$store.getters['board/currentConfig'];
        let savedFilterItem;

        if (currentConfig.customGroupingOptions && currentConfig.customGroupingOptions.length > 0) {
          const isExistFilter = currentConfig.customGroupingOptions.some(
            item => item.fieldId === selectedItem.value && item.isCustomField === selectedItem.isCustomField
          );

          if (isExistFilter) {
            savedFilterItem = currentConfig.customGroupingOptions.find(
              item => item.fieldId === selectedItem.value && item.isCustomField === selectedItem.isCustomField
            );
          }
        }

        this.$store.dispatch('board/clearCustomSort');

        this.$store.dispatch('board/setBoardConfig', {
          config: {
            ...this.$store.getters['board/currentConfig'],
            grouping: [savedFilterItem || {
              fieldId: selectedItem.value,
              isCustomField: selectedItem.isCustomField,
              order: [],
            }],
            sorting: {
              ...this.$store.getters['board/currentConfig'].sorting,
              order: [],
            },
          },
        });

        break;
      }
      case 'board_sort_value': {
        this.toggleContextMenu(item, type, menu);
        userExtAnalytics.log('header_select_board_sorting', {
          type: selectedItem.name,
        });
        this.$store.dispatch('board/setBoardConfig', {
          config: {
            ...this.$store.getters['board/currentConfig'],
            sorting: {
              values: [{
                fieldId: selectedItem.value,
                direction: 'ASC',
              }],
            },
          },
        });
        break;
      }
      }
    },
    selectItemTrigger(context, item, e) {
      e.stopPropagation();

      if (e.type === 'mouseenter' && item.menu === 'main') {
        const moveToContext = this.contextMenus.find(ctx => ctx.menu === 'moveTo');

        if (['board_column_move_to', 'board_task_move_to'].includes(item.id)) {
          if (moveToContext) return;
        } else {
          if (moveToContext) {
            const { item, type, menu } = moveToContext;

            this.toggleContextMenu(item, type, menu);
          }

          return;
        }
      }

      this.selectItem(context, item, e);
    },
    mouseHandlers(context, item) {
      const events = item.handlers || ['click'];

      return events.reduce((result, event) => {
        result[event] = e => this.selectItemTrigger(context, item, e);

        return result;
      }, {});
    },
    getPopupStyle({ type, menu }) {
      let width = 260;

      if ((type === 'task' && ['moveTo', 'status', 'priority'].includes(menu)) || (type === 'column' && menu === 'moveTo')) {
        width = 180;
      }

      if (type === 'group') {
        width = 220;
      }

      if (type === 'sort') {
        width = 240;
      }

      return {
        width: `${width}px`,
        maxHeight: '272px',
        overflow: menu === 'moveTo' ? 'hidden' : 'auto',
      };
    },
    selectedMultiselect(values, context) {
      const { item, type, menu } = context;

      this.toggleContextMenu(item, type, menu);

      const key = this.groupingFieldInfo.isCustom ? this.groupingFieldInfo.customFieldId : this.groupingFieldInfo.key;
      let value = '';

      if (key === 'resources') {
        changeAssign({
          task: item,
          rawValue: values.map(i => i.value),
          $store: this.$store,
        });

        return;
      }

      value = prepareTaskValue({
        task: item,
        key,
        rawValue: values.map(i => i.value),
        field: this.groupingFieldInfo,
      });

      this.$emit('taskChange', {
        taskData: item,
        key,
        value,
      });
    },
    getUniqueKey(item) {
      return `${item.id}_${item.value || ''}`; 
    },
  },
};
</script>

<style module src="../less/contextMenu.less"></style>
